export interface NavigationState {
  path: string
  title: string
  timestamp: number
}

const getStorageKey = (type: string) => `${type}-from`

export const saveNavigationState = (type: string, path: string, title: string): void => {
  const state: NavigationState = {
    path,
    title,
    timestamp: Date.now(),
  }

  try {
    const existingState = getNavigationState(type)
    if (existingState && existingState.path !== path) {
      const parentState: NavigationState = {
        path: existingState.path,
        title: existingState.title,
        timestamp: existingState.timestamp,
      }
      sessionStorage.setItem(`${type}-parent`, JSON.stringify(parentState))
    }
    sessionStorage.setItem(getStorageKey(type), JSON.stringify(state))
  } catch (error) {
    console.warn("Failed to save navigation state:", error)
  }
}

export const getNavigationState = (type: string): NavigationState | null => {
  try {
    const stored = sessionStorage.getItem(getStorageKey(type))
    if (!stored) return null

    const state: NavigationState = JSON.parse(stored)

    const oneHourAgo = Date.now() - 60 * 60 * 1000
    if (state.timestamp < oneHourAgo) {
      clearNavigationState(type)
      return null
    }

    return state
  } catch (error) {
    console.warn("Failed to parse navigation state:", error)
    return null
  }
}

export const clearNavigationState = (type: string): void => {
  try {
    sessionStorage.removeItem(getStorageKey(type))
    sessionStorage.removeItem(`${type}-parent`)
  } catch (error) {
    console.warn("Failed to clear navigation state:", error)
  }
}

export const getParentNavigationState = (type: string): NavigationState | null => {
  try {
    const stored = sessionStorage.getItem(`${type}-parent`)
    if (!stored) return null

    const state: NavigationState = JSON.parse(stored)

    const oneHourAgo = Date.now() - 60 * 60 * 1000
    if (state.timestamp < oneHourAgo) {
      sessionStorage.removeItem(`${type}-parent`)
      return null
    }

    return state
  } catch (error) {
    console.warn("Failed to parse parent navigation state:", error)
    return null
  }
}

export const getBackPath = (type: string, defaultPath: string): string => {
  const state = getNavigationState(type)
  const parentState = getParentNavigationState(type)
  return state?.path || parentState?.path || defaultPath
}

export const getBackTitle = (type: string, defaultTitle: string): string => {
  const state = getNavigationState(type)
  const parentState = getParentNavigationState(type)
  return state?.title || parentState?.title || defaultTitle
}

// Line Items
// ------------------------------------------------------------
export const getLineItemsNavigationState = (): NavigationState | null => {
  return getNavigationState("line-items")
}

export const clearLineItemsNavigationState = (): void => {
  clearNavigationState("line-items")
}

export const getLineItemsBackPath = (): string => {
  return getBackPath("line-items", "/line-items")
}

export const getLineItemsBackTitle = (): string => {
  return getBackTitle("line-items", "Line Items")
}

// Menus
// ------------------------------------------------------------
export const getMenusNavigationState = (): NavigationState | null => {
  return getNavigationState("menus")
}

export const clearMenusNavigationState = (): void => {
  clearNavigationState("menus")
}

export const getMenusBackPath = (): string => {
  return getBackPath("menus", "/menus")
}

export const getMenusBackTitle = (): string => {
  return getBackTitle("menus", "Menus")
}

// Categories
// ------------------------------------------------------------
export const getCategoriesNavigationState = (): NavigationState | null => {
  return getNavigationState("categories")
}

export const clearCategoriesNavigationState = (): void => {
  clearNavigationState("categories")
}

export const getCategoriesBackPath = (): string => {
  return getBackPath("categories", "/categories")
}

export const getCategoriesBackTitle = (): string => {
  return getBackTitle("categories", "Categories")
}

// Packs
// ------------------------------------------------------------
export const getPacksNavigationState = (): NavigationState | null => {
  return getNavigationState("packs")
}

export const clearPacksNavigationState = (): void => {
  clearNavigationState("packs")
}

export const getPacksBackPath = (): string => {
  return getBackPath("packs", "/packs")
}

export const getPacksBackTitle = (): string => {
  return getBackTitle("packs", "Packs")
}

// Bookings
// ------------------------------------------------------------
export const getBookingsNavigationState = (): NavigationState | null => {
  return getNavigationState("bookings")
}

export const clearBookingsNavigationState = (): void => {
  clearNavigationState("bookings")
}

export const getBookingsBackPath = (): string => {
  return getBackPath("bookings", "/bookings")
}

export const getBookingsBackTitle = (): string => {
  return getBackTitle("bookings", "Bookings")
}

// Clients
// ------------------------------------------------------------
export const getClientsNavigationState = (): NavigationState | null => {
  return getNavigationState("clients")
}

export const clearClientsNavigationState = (): void => {
  clearNavigationState("clients")
}

export const getClientsBackPath = (): string => {
  return getBackPath("clients", "/clients")
}

export const getClientsBackTitle = (): string => {
  return getBackTitle("clients", "Clients")
}

// Areas
// ------------------------------------------------------------
export const getAreasNavigationState = (): NavigationState | null => {
  return getNavigationState("areas")
}

export const clearAreasNavigationState = (): void => {
  clearNavigationState("areas")
}

export const getAreasBackPath = (): string => {
  return getBackPath("areas", "/areas")
}

export const getAreasBackTitle = (): string => {
  return getBackTitle("areas", "Areas")
}

// Sponsorship
// ------------------------------------------------------------
export const getSponsorshipsNavigationState = (): NavigationState | null => {
  return getNavigationState("sponsorship")
}

export const clearSponsorshipsNavigationState = (): void => {
  clearNavigationState("sponsorship")
}

export const getSponsorshipsBackPath = (): string => {
  return getBackPath("sponsorship", "/sponsorship")
}

export const getSponsorshipsBackTitle = (): string => {
  return getBackTitle("sponsorship", "Sponsorship")
}
